import { useState, useRef, useEffect } from 'react';
import { computeOptionsMenuPosition } from '../lib/computeOptionsMenuPosition';

/**
 * Options-menu (gear / mobile-options popover) state + listeners extracted from
 * EventListModule (#3277).
 *
 * Owns the three pieces of state the inline orchestrator kept (`showOptionsMenu`,
 * `optionsMenuPosition`, `optionsMenuRef`) plus the two effects that drive them:
 *
 *   1. The `xavior:open-module-options` window listener — positions the popover
 *      (via the pure `computeOptionsMenuPosition` math) and opens it, but only
 *      for THIS module id. Reads `moduleRootRef.current` lazily inside the
 *      handler for the fallback placement, exactly as the inline closure did.
 *      Dep array stays `[moduleId]` (the inline effect's `[module.id]`).
 *   2. The document `mousedown` click-outside listener — closes the menu unless
 *      the click is inside the menu or on an item delete button. Dep array stays
 *      `[showOptionsMenu]`.
 *
 * Delegation/identity notes (behavior-preserving):
 * - Same hook-call position relative to the inline effects: this is called once
 *   in the orchestrator BEFORE `useCalendarSync` (which still receives
 *   `showOptionsMenu`). Hook order/count is stable.
 * - The listener registration/cleanup, the `moduleId !== ` early-return, the
 *   `anchorRect`-vs-`moduleRect` precedence, and the click-outside guard chain
 *   (`!showOptionsMenu` → ref.contains → `button[title="Delete"]`) are unchanged.
 * - `optionsMenuRef` is a stable ref created here and returned by identity so the
 *   menu container the JSX attaches it to is the same node the click-outside
 *   guard reads — no ref-identity change.
 */
export interface UseEventOptionsMenuOptions {
  moduleId: string;
  moduleRootRef: React.RefObject<HTMLDivElement | null>;
}

export function useEventOptionsMenu(opts: UseEventOptionsMenuOptions) {
  const { moduleId, moduleRootRef } = opts;

  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const optionsMenuRef = useRef<HTMLDivElement>(null);
  const [optionsMenuPosition, setOptionsMenuPosition] =
    useState<{ top: number; left: number } | null>(null);

  // Listen for mobile module options open event
  useEffect(() => {
    const handleOpenOptions = (event: CustomEvent) => {
      const {
        moduleId: eventModuleId,
        anchorRect,
      } = (event.detail || {}) as {
        moduleId?: string;
        anchorRect?: { top: number; right: number; bottom: number; left: number };
      };
      if (eventModuleId !== moduleId) return;

      const moduleRect = !anchorRect && moduleRootRef.current
        ? moduleRootRef.current.getBoundingClientRect()
        : null;

      const { top, left } = computeOptionsMenuPosition(
        window.innerWidth,
        window.innerHeight,
        anchorRect,
        moduleRect,
      );

      setOptionsMenuPosition({ top, left });
      setShowOptionsMenu(true);
    };
    window.addEventListener('xavior:open-module-options', handleOpenOptions as EventListener);
    return () => window.removeEventListener('xavior:open-module-options', handleOpenOptions as EventListener);
  }, [moduleId]);

  // Close options menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showOptionsMenu) return;

      const target = event.target as HTMLElement;

      // Don't close if clicking the options menu dropdown
      if (optionsMenuRef.current?.contains(target)) return;

      // Don't close if clicking a delete button on items
      const isDeleteButton = target.closest('button[title="Delete"]');
      if (isDeleteButton) return;

      // Close the menu for any other click
      setShowOptionsMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOptionsMenu]);

  return {
    showOptionsMenu,
    setShowOptionsMenu,
    optionsMenuPosition,
    optionsMenuRef,
  };
}
